import React, { useState, useEffect, useRef } from 'react';
import BaseTruckScheduleForm from '../../components/form/BaseTruckScheduleForm';
import { TextField, SelectField, CheckboxField } from '../../components/form/FormFields';
import { REF_LIST_MAX_LENGTH, REF_LIST_HELPER_TEXT } from '../../components/utils/refListUtils';

/**
 * Form component for scheduling outbound trucks
 * This component is the source of truth for outbound-specific fields (order_id, customer, destination)
 */
const OutboundScheduleForm = ({ onClose, onSave, preSelectedDate, isLoading, truckData }) => {
  const today = new Date();
  const formattedToday = today.toISOString().split('T')[0];

  // Create a ref to store the latest form values
  // This ensures we always have access to the latest values, even during validation and submission
  const formValuesRef = useRef({
    po: '',
    order_number: '',
    customer: '',
    destination: '',
    staging_area: '',
    priority: 'Normal',
    load_material: ''
  });

  // State for Small Parcel checkbox
  const [isSmallParcel, setIsSmallParcel] = useState(false);

  // State for form data
  const [formData, setFormData] = useState({
    business_unit: '',
    carrier: '',
    load_type: 'Live Load',
    size: 1,
    po: '',  // Optional for outbound
    order_number: '',  // Optional for outbound
    customer: '',  // Optional for outbound
    destination: '', // Optional for outbound
    scheduled_date: preSelectedDate || formattedToday,
    scheduled_time: '09:00',
    dock_door: '',
    staging_area: '',
    priority: 'Normal',
    secure: false,
    scheduler_notes: '',
    status: 'Scheduled',
    is_small_parcel: false, // New field for small parcel flag
    load_material: '' // New field for load material type
  });
  
  // State for form errors
  const [errors, setErrors] = useState({});
  
  // Effect to update preSelectedDate when it changes
  useEffect(() => {
    if (preSelectedDate) {
      setFormData(prev => ({
        ...prev,
        scheduled_date: preSelectedDate
      }));
    }
  }, [preSelectedDate]);
  
  // Effect to track the current form data for debugging and update ref
  useEffect(() => {
    // Log only in development mode
    if (process.env.NODE_ENV === 'development') {
      console.log('OutboundScheduleForm current data:', { 
        po: formData.po,
        order_number: formData.order_number,
        customer: formData.customer,
        destination: formData.destination,
        load_material: formData.load_material, // Explicitly log load_material
        all_form_data: formData // Log all form data for better debugging
      });
    }
    
    // Update our ref with the latest values
    formValuesRef.current = {
      po: formData.po,
      order_number: formData.order_number,
      customer: formData.customer,
      destination: formData.destination,
      staging_area: formData.staging_area,
      priority: formData.priority,
      load_material: formData.load_material
    };
    
    // IMPORTANT: Also update BaseTruckScheduleForm's state for load_material
    // This ensures the value is always synchronized between both components
    if (window.truckScheduleFormSetData && formData.load_material) {
      window.truckScheduleFormSetData(prev => ({
        ...prev,
        load_material: formData.load_material
      }));
    }
  }, [formData]);
  
  // Handle change in all form fields
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Store the updated value
    const updatedValue = type === 'checkbox' ? checked : value;

    // Special handling for small parcel checkbox
    if (name === 'is_small_parcel') {
      setIsSmallParcel(checked);

      // When enabling small parcel, set load_type to 'Parcel' and load_material to 'Small Parcel'
      if (checked) {
        setFormData(prev => ({
          ...prev,
          load_type: 'Parcel',
          load_material: 'Small Parcel',
          [name]: checked
        }));
        
        // Also update the parent form state
        if (window.truckScheduleFormSetData) {
          window.truckScheduleFormSetData(prev => ({
            ...prev,
            load_material: 'Small Parcel'
          }));
        }
      } else {
        // When unchecking small parcel, don't change load_material
        // Just update the is_small_parcel flag
        setFormData(prev => ({
          ...prev,
          [name]: checked
          // Importantly, we're NOT changing load_material here
        }));
      }
    } else {
      // Regular field update
      setFormData(prev => ({
        ...prev,
        [name]: updatedValue
      }));
    }

    // Also update our ref immediately for direct access
    if (['po', 'order_number', 'customer', 'destination', 'staging_area', 'priority', 'load_material'].includes(name)) {
      formValuesRef.current[name] = updatedValue;
      
      // Debug log for load_material changes
      if (name === 'load_material' && process.env.NODE_ENV === 'development') {
        console.log(`Load material set in formValuesRef: ${updatedValue}`);
      }
    }

    // Clear error when field is changed
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }

    // Log the change for debugging only in development mode
    if (process.env.NODE_ENV === 'development') {
      console.log(`OutboundScheduleForm field "${name}" changed:`, {
        newValue: updatedValue,
        formData: { ...formData, [name]: updatedValue }
      });
    }
  };
  
  // Load types specific to outbound
  const loadTypes = ['Live Load', 'Container Drop', 'Parcel Pickup'];
  
  // Load material types
  const loadMaterialTypes = ['Pallet', 'Oversized Pallet', 'Gaylord', 'Small Parcel', 'Various'];
  
  // Priority levels
  const priorityLevels = ['Low', 'Normal', 'High', 'Urgent'];
  
  /**
   * Custom validation for outbound trucks
   * This function operates on the parent form data but uses our local state
   * for outbound-specific fields (order_id, customer, destination)
   */
  const validateForm = (parentFormData) => {
    const errors = {};

    // Use our form values ref to ensure we have the most up-to-date values
    const currentOutboundFields = formValuesRef.current;

    // The complete data to validate combines the parent form data with our outbound-specific fields
    const dataToValidate = {
      ...parentFormData,
      // Override with our outbound-specific fields - these are the source of truth
      po: currentOutboundFields.po,
      order_number: currentOutboundFields.order_number,
      customer: currentOutboundFields.customer,
      destination: currentOutboundFields.destination,
      staging_area: currentOutboundFields.staging_area,
      priority: currentOutboundFields.priority,
      // Always include load_material from either the ref, form state, or parent form
      load_material: currentOutboundFields.load_material || formData.load_material || parentFormData.load_material || '',
      is_small_parcel: isSmallParcel // Include small parcel flag
    };

    // Log the form data being validated only in development mode
    if (process.env.NODE_ENV === 'development') {
      console.log('OutboundScheduleForm validating:', {
        parentFormData: parentFormData,
        currentOutboundFields: currentOutboundFields,
        isSmallParcel: isSmallParcel,
        finalData: dataToValidate,
        load_material_sources: {
          fromRef: currentOutboundFields.load_material,
          fromState: formData.load_material,
          fromParent: parentFormData.load_material,
          final: dataToValidate.load_material
        }
      });
    }

    // Standard field validation
    if (!dataToValidate.business_unit) {
      errors.business_unit = 'Business unit is required';
    }

    if (!dataToValidate.carrier) {
      errors.carrier = 'Carrier is required';
    }

    if (!dataToValidate.load_type) {
      errors.load_type = 'Load type is required';
    }

    if (!dataToValidate.size || dataToValidate.size < 1) {
      errors.size = 'Size must be at least 1';
    }

    if (!dataToValidate.scheduled_date) {
      errors.scheduled_date = 'Scheduled date is required';
    }

    if (!dataToValidate.scheduled_time) {
      errors.scheduled_time = 'Scheduled time is required';
    }

    // Outbound-specific fields - PO is optional for all outbound trucks
    // No validation required for PO (making it optional)

    // For small parcels, ensure business unit is either INCOMM or Various Business Units (9999)
    if (isSmallParcel && dataToValidate.business_unit) {
      const businessUnitId = String(dataToValidate.business_unit);
      // Check if business unit is not INCOMM (we'll need to add this check when we have the INCOMM ID)
      // This will be handled later when we modify the UI to only show valid options
    }

    // Customer and Destination are optional fields

    // Log validation results only in development mode
    if (process.env.NODE_ENV === 'development') {
      console.log('OutboundScheduleForm validation results:', {
        hasErrors: Object.keys(errors).length > 0,
        errors,
        isSmallParcel: isSmallParcel
      });
    }

    return errors;
  };
  
  /**
   * Submit handler for outbound truck form
   * This is the critical function that ensures outbound-specific fields are included
   * in the data sent to the server
   */
  const handleSubmit = async (parentTruckData) => {
    // Use our form values ref to ensure we have the most up-to-date values
    const currentOutboundFields = formValuesRef.current;

    // Comprehensive debug logging to track the exact submission flow only in development mode
    if (process.env.NODE_ENV === 'development') {
      console.log('OutboundScheduleForm handleSubmit called with data:', {
        parentData: parentTruckData,
        currentOutboundFields: currentOutboundFields,
        isSmallParcel: isSmallParcel
      });
    }

    // PO is now optional for all outbound trucks, no validation needed here

    try {
      // Create the complete truck data to submit
      // IMPORTANT: We override any outbound-specific fields from the parent with our local values
      const completeData = {
        ...parentTruckData,
        // ALWAYS use our local state values for outbound-specific fields
        po: currentOutboundFields.po,
        order_number: currentOutboundFields.order_number,
        customer: currentOutboundFields.customer,
        destination: currentOutboundFields.destination,
        staging_area: currentOutboundFields.staging_area,
        priority: currentOutboundFields.priority,
        // For now, just include a placeholder - we'll set the final value below
        load_material: '',
        status: 'Scheduled', // Ensure status is set for the server
        is_small_parcel: isSmallParcel // Include small parcel flag
      };
      
      // CRITICAL FIX: Directly set the load_material based on the checkbox state
      if (isSmallParcel) {
        // If small parcel is checked, always use Small Parcel regardless of selection
        completeData.load_material = 'Small Parcel';
      } else {
        // Otherwise use the selected value from any available source
        // Access the DOM directly as a last resort if needed
        const domValue = document.getElementById('load_material')?.value;
        completeData.load_material = currentOutboundFields.load_material || 
                                     formData.load_material || 
                                     parentTruckData.load_material || 
                                     domValue || 
                                     window.__selectedLoadMaterial || 
                                     '';
      }
      
      console.log('OutboundScheduleForm: Ensuring load_material is set:', {
        isSmallParcel,
        refValue: currentOutboundFields.load_material,
        formValue: formData.load_material,
        parentValue: parentTruckData.load_material,
        domValue: document.getElementById('load_material')?.value,
        finalValue: completeData.load_material
      });

      // Special handling for "Various Business Units" option when small parcel is checked
      if (isSmallParcel && completeData.business_unit === 'Various Business Units') {
        completeData.business_unit = '9999';
      }
      
      // Enhanced logging before submission only in development mode
      if (process.env.NODE_ENV === 'development') {
        console.log('OutboundScheduleForm submitting final data:', {
          business_unit: completeData.business_unit,
          carrier: completeData.carrier,
          scheduled_at: completeData.scheduled_at,
          // Outbound-specific fields
          po: completeData.po,
          order_number: completeData.order_number,
          customer: completeData.customer,
          destination: completeData.destination,
          // Added explicit log for load_material field
          load_material: completeData.load_material,
          is_small_parcel: completeData.is_small_parcel,
          // Complete data
          allFields: completeData
        });
        
        // Special additional log for load_material field
        console.log('OutboundScheduleForm FINAL load_material check:', completeData.load_material);
      }
      
      // Call the parent's onSave function with our complete data
      const result = await onSave(completeData);
      
      // Handle server validation errors
      if (result && typeof result === 'object' && result.errors) {
        if (process.env.NODE_ENV === 'development') {
          console.error('OutboundScheduleForm server returned errors:', result.errors);
        }
        setErrors(result.errors);
        return false;
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('OutboundScheduleForm submission successful');
      }
      return result;
    } catch (error) {
      // Always log errors but with less detail in production
      if (process.env.NODE_ENV === 'development') {
        console.error('OutboundScheduleForm submission error:', error);
      } else {
        console.error('Form submission error occurred');
      }
      setErrors({ general: error.message || 'An error occurred during submission' });
      return false;
    }
  };
  
  /**
   * Creates the custom fields JSX for outbound-specific fields
   * Using a function ensures we always render with the most current state
   */
  const renderCustomFields = () => (
    <>
      {/* Small Parcel Checkbox in a container */}
      <div className="mb-4">
        <div className="bg-slate-700/30 p-4 rounded-lg border border-slate-600/50">
          <CheckboxField
            label="Small Parcel (limits Business Unit options and simplifies form)"
            id="is_small_parcel"
            name="is_small_parcel"
            checked={formData.is_small_parcel || false}
            onChange={handleChange}
            error={errors.is_small_parcel}
            disabled={isLoading}
          />
        </div>
      </div>

      {/* Only show Outbound Specific Information if not a small parcel */}
      {!isSmallParcel && (
        <div className="bg-slate-700/30 p-4 rounded-lg mb-4 border border-slate-600/50">
          <h3 className="text-sm font-medium text-slate-300 mb-3 border-b border-slate-600/50 pb-2">Outbound Specific Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
            {/* Multi-order trucks enter these as comma-separated lists, positionally paired
                (po[i] belongs to order_number[i]). maxLength mirrors the DB column so an
                over-long list can't reach the server. See Multi_Order_Outbound_Trucks.md. */}
            <TextField
              label="PO"
              id="po"
              name="po"
              placeholder="Enter PO"
              value={formData.po || ''}
              onChange={handleChange}
              error={errors.po}
              required={false}
              disabled={isLoading}
              maxLength={REF_LIST_MAX_LENGTH}
              helperText={REF_LIST_HELPER_TEXT}
            />

            <TextField
              label="Order Number"
              id="order_number"
              name="order_number"
              placeholder="Enter order number"
              value={formData.order_number || ''}
              onChange={handleChange}
              error={errors.order_number}
              required={false}
              disabled={isLoading}
              maxLength={REF_LIST_MAX_LENGTH}
              helperText={REF_LIST_HELPER_TEXT}
            />

            <TextField
              label="Customer"
              id="customer"
              name="customer"
              placeholder="Enter customer name"
              value={formData.customer || ''}
              onChange={handleChange}
              error={errors.customer}
              disabled={isLoading}
            />

            <TextField
              label="Destination"
              id="destination"
              name="destination"
              placeholder="Enter destination"
              value={formData.destination || ''}
              onChange={handleChange}
              error={errors.destination}
              disabled={isLoading}
            />

            <TextField
              label="Staging Area"
              id="staging_area"
              name="staging_area"
              placeholder="Enter staging area"
              value={formData.staging_area || ''}
              onChange={handleChange}
              error={errors.staging_area}
              disabled={isLoading}
            />
          
            <div className="md:col-span-2">
              <SelectField
                label="Load Material"
                id="load_material"
                name="load_material"
                value={formData.load_material || ''}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  
                  // Update local state
                  handleChange(e);
                  
                  // Update the parent form state 
                  if (window.truckScheduleFormSetData) {
                    window.truckScheduleFormSetData(prev => ({
                      ...prev,
                      load_material: selectedValue
                    }));
                  }
                  
                  // Update our form values ref directly
                  formValuesRef.current.load_material = selectedValue;
                  
                  // Store in a global variable as a last resort
                  window.__selectedLoadMaterial = selectedValue;
                  
                  console.log('OutboundScheduleForm: Load material selected:', selectedValue);
                }}
                options={loadMaterialTypes}
                error={errors.load_material}
                required={false}
                disabled={isLoading}
              />
            </div>

            <div className="md:col-span-2">
              <SelectField
                label="Priority"
                id="priority"
                name="priority"
                value={formData.priority || 'Normal'}
                onChange={handleChange}
                options={priorityLevels}
                error={errors.priority}
                disabled={isLoading}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );

  // Find INCOMM business unit
  const [incommBusinessUnit, setIncommBusinessUnit] = useState(null);
  const [filteredBusinessUnits, setFilteredBusinessUnits] = useState([]);

  // Effect to set up filtered business units for small parcel option
  useEffect(() => {
    // This would be called when the base form loads business units
    // We'll intercept the business units from the parent form's render props
    if (window.businessUnits && window.businessUnits.length > 0) {
      // Find the INCOMM business unit
      const incomm = window.businessUnits.find(unit =>
        unit.name.toLowerCase() === 'incomm' ||
        unit.name.toLowerCase().includes('incomm')
      );

      if (incomm) {
        setIncommBusinessUnit(incomm);
        if (process.env.NODE_ENV === 'development') {
          console.log('Found INCOMM business unit:', incomm);
        }
      }

      // If small parcel is selected, filter business units
      if (isSmallParcel) {
        let filtered = [];

        // Add INCOMM if found
        if (incomm) {
          filtered.push(incomm);
        }

        // Add "Various Business Units" option
        filtered.push({ id: '9999', name: 'Various Business Units' });

        setFilteredBusinessUnits(filtered);
      } else {
        // Use all business units if small parcel not selected
        setFilteredBusinessUnits(window.businessUnits);
      }
    }
  }, [isSmallParcel, window.businessUnits]);

  // Effect to listen for business units loaded by BaseTruckScheduleForm
  useEffect(() => {
    // Create a custom event listener to capture business units when loaded
    const handleBusinessUnitsLoaded = (event) => {
      if (event.detail && Array.isArray(event.detail)) {
        window.businessUnits = event.detail;

        // Find INCOMM business unit
        const incomm = event.detail.find(unit =>
          unit.name.toLowerCase() === 'incomm' ||
          unit.name.toLowerCase().includes('incomm')
        );

        if (incomm) {
          setIncommBusinessUnit(incomm);
          if (process.env.NODE_ENV === 'development') {
            console.log('Event captured: Found INCOMM business unit:', incomm);
          }
        }

        // Update filtered business units based on small parcel selection
        if (isSmallParcel) {
          let filtered = [];

          // Add INCOMM if found
          if (incomm) {
            filtered.push(incomm);
          }

          // Add "Various Business Units" option
          filtered.push({ id: '9999', name: 'Various Business Units' });

          setFilteredBusinessUnits(filtered);
        } else {
          // Use all business units
          setFilteredBusinessUnits(event.detail);
        }
      }
    };

    // Add the event listener
    window.addEventListener('businessUnitsLoaded', handleBusinessUnitsLoaded);

    // Clean up
    return () => {
      window.removeEventListener('businessUnitsLoaded', handleBusinessUnitsLoaded);
    };
  }, [isSmallParcel]);
  
  // Listen for load_material changes in BaseTruckScheduleForm
  useEffect(() => {
    // Find the load_material field and listen for changes
    const loadMaterialField = document.getElementById('load_material');
    if (loadMaterialField) {
      // Create a mutation observer to watch for attribute changes
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
            const newValue = loadMaterialField.value;
            if (newValue && newValue !== formData.load_material) {
              // Update our local state
              setFormData(prev => ({
                ...prev,
                load_material: newValue
              }));
              
              // Update our ref immediately
              formValuesRef.current.load_material = newValue;
              
              console.log('OutboundScheduleForm: Captured load_material change from BaseTruckScheduleForm:', newValue);
            }
          }
        });
      });
      
      // Configure the observer to watch for attribute changes
      observer.observe(loadMaterialField, { 
        attributes: true,
        attributeFilter: ['value']
      });
      
      // Clean up
      return () => {
        observer.disconnect();
      };
    }
  }, []);

  // Log final state before rendering only in development mode
  if (process.env.NODE_ENV === 'development') {
    console.log('OutboundScheduleForm rendering with form data:', {
      state: formData,
      ref: formValuesRef.current,
      isSmallParcel,
      incommBusinessUnit,
      filteredBusinessUnits
    });
  }

  // Override the default business units with our filtered list when small parcel is selected
  const overrideProps = isSmallParcel ? {
    businessUnitsOverride: filteredBusinessUnits.length > 0 ? filteredBusinessUnits : undefined
  } : {};

  return (
    <BaseTruckScheduleForm
      onClose={onClose}
      onSave={handleSubmit}
      preSelectedDate={preSelectedDate}
      isLoading={isLoading}
      truckData={truckData}
      mode="outbound"
      initialValues={formData}
      customFields={renderCustomFields()}
      loadTypes={loadTypes}
      loadMaterialTypes={loadMaterialTypes}
      validateForm={validateForm}
      title="Schedule New Outbound Truck"
      {...overrideProps}
    />
  );
};

export default OutboundScheduleForm;
